import type { PlaceSource } from '../shared/place_search/my_location_row.js';
import type { PlaceSuggestion } from '../shared/place_search/place_panel.js';
import type {
  OfferBadgeIcon,
  OfferBadgeView,
  OfferPolicyTone,
} from '../shared/results_page/offer_badge.js';

export type HotelAsyncState = 'idle' | 'loading' | 'ready' | 'error';
export type HotelPolicyTone = OfferPolicyTone;
export type HotelAmenityIcon =
  | 'accessibility'
  | 'air-conditioning'
  | 'bar'
  | 'breakfast'
  | 'business'
  | 'family'
  | 'fitness'
  | 'parking'
  | 'pets'
  | 'pool'
  | 'restaurant'
  | 'spa'
  | 'wifi'
  | 'other';

export type HotelCoordinates = { latitude: number; longitude: number };
export type HotelMapLocation = { coordinates: HotelCoordinates; label: string };
export type HotelPhotoView = { id: string; url: string; alt: string };
/** O selo do eixo é o da casca de resultados (`OfferBadgeView`): hotel e carro
 *  falam o mesmo vocabulário de selos. */
export type HotelBadgeIcon = OfferBadgeIcon;
export type HotelBadgeView = OfferBadgeView;
export type HotelAmenityView = { id: string; label: string; icon: HotelAmenityIcon };
/** `label` é a frase acessível ("nota 4,2"); `valueLabel` é só o número
 *  formatado, para as superfícies que mostram a ESTRELA no lugar da palavra;
 *  `bandLabel` é a faixa por extenso ("Excelente"), para quem compõe a linha
 *  peça a peça (a identidade da Página do Hotel: ★ nota em negrito + faixa +
 *  contagem apagada). */
export type HotelRatingView = {
  label: string;
  valueLabel?: string;
  bandLabel?: string;
  value?: number;
  reviewsLabel?: string;
};
/** Uma linha do campo `Onde` — desde a padronização do campo de lugar o tipo
 *  canônico é o `PlaceSuggestion` do `shared/place_search` (a mesma linha serve
 *  as praças do aéreo); este nome segue como o vocabulário do hotel. */
export type HotelPlaceSuggestion = PlaceSuggestion;
export type HotelTravelerOption = {
  id: string;
  label: string;
  description?: string;
  /** A foto do perfil (#2640), quando o host a costurou; `null`/ausente ⇒ iniciais. */
  photoUrl?: string | null;
};

/** Ocupação do quarto em HÓSPEDES, sem distinguir idade — mesmo vocabulário do
 *  wire (`HotelRoomOccupancySchema`). O par `adults`/`childrenAges` que existia
 *  aqui carregava uma idade que ninguém lia e um contador que excluía a criança. */
export type HotelRoomOccupancyView = { guests: number };
export type HotelMoneyView = { amount: number; currency: string };

export type HotelSearchValue = {
  place: string;
  selectedPlaceId?: string;
  /** A origem do lugar escolhido (ver `PlaceSource`): o pick da lista a apaga. */
  placeSource?: PlaceSource;
  checkin?: string;
  checkout?: string;
  rooms: readonly HotelRoomOccupancyView[];
  travelerIds: readonly string[];
  policyTravelerId?: string;
};

export type HotelOfferView = {
  id: string;
  name: string;
  summary?: string;
  locationLabel?: string;
  photos: readonly HotelPhotoView[];
  coordinates?: HotelCoordinates;
  rating?: HotelRatingView;
  distanceLabel?: string;
  travelTimeLabel?: string;
  priceLabel: string;
  priceDetail?: string;
  badges: readonly HotelBadgeView[];
  /** O hotel É favorito do viajante da busca corrente — o estado que o coração
   *  reflete (`aria-pressed`). Distinto do selo: o selo é UM por card e o
   *  favorito o vence; o coração existe em todo card quando o host liga o
   *  toggle (`HotelFavoriteToggle`), mesmo apagado. */
  favorite?: boolean;
  /** Fora da política. Não é chip: vira escudo sem palavra colado no preço
   *  (protótipo `CardUnicoDoHotel`). O rótulo É a marca — escudo sem nome
   *  acessível não pode existir, então não há booleano separado. */
  outOfPolicyLabel?: string;
  /** O motivo concreto da exceção ("Acima do teto de 800 por noite"), no tooltip
   *  do escudo. Ausente quando a oferta não traz as regras quebradas. */
  outOfPolicyReason?: string;
  /** A condição de reembolso do hotel, em palavras: "Reembolsável" quando a
   *  tarifa exibida já é, "Reembolsável a partir de R$ X" quando é OUTRA
   *  tarifa, mais cara (#3404). Ausente = o dado não veio; o card não inventa. */
  refundableLabel?: string;
  /** O hotel tem acordo com a empresa (#6359), em palavras. À parte dos
   *  `badges` porque a linha da lista (#6928) o diz sem selo, e lá o favorito
   *  ou a estadia não o apagam. Ausente = sem acordo. */
  agreedRateLabel?: string;
  amenities: readonly HotelAmenityView[];
  detailsAvailable: boolean;
  bookable: boolean;
};

/** O toggle do coração, ligado pelo HOST — presente, todo card de resultado
 *  ganha o botão; ausente (caminho conversacional, storybook), nenhum coração
 *  existe. O estado corrente vem de `HotelOfferView.favorite`; os rótulos são
 *  o NOME ACESSÍVEL do gesto em cada estado (`aria-pressed` carrega o estado,
 *  o rótulo diz a ação). Sem IO aqui: o kit só devolve o `hotelId` clicado. */
export type HotelFavoriteToggle = {
  markLabel: string;
  unmarkLabel: string;
  onToggle(hotelId: string): void;
};

export type HotelMapView = {
  available: boolean;
  accessToken?: string;
  center?: HotelCoordinates;
  anchorLabel?: string;
  contextual?: boolean;
  /** O enquadramento é de quem buscou (#3984): o conjunto veio da área VISÍVEL do
   *  mapa ("buscar nesta área"), então a câmera já está onde a pessoa a deixou.
   *  Só o primeiro quadro de cada instância enquadra; trocar o conjunto (a
   *  resposta da área, um filtro) não move a câmera. */
  keepFrame?: boolean;
  fallbackLabel: string;
};

/**
 * O que a tarifa entrega, em ENUM — nunca o texto do fornecedor.
 *
 * O chip sai inteiro daqui: o valor escolhe o rótulo (na `copy`) e o tom (verde
 * incluído / vermelho ausente). Antes o par era booleano + rótulo livre, e o
 * rótulo vinha do regime do fornecedor — então a mesma ausência de café
 * imprimia "Apenas quarto" em vermelho num card e "Café da manhã continental"
 * em verde no outro. O texto do fornecedor continua existindo, em `planLabel`:
 * é o TÍTULO da linha da tarifa, onde variar é informação.
 */
export type HotelRateBreakfast = 'WITH_BREAKFAST' | 'WITHOUT_BREAKFAST';
export type HotelRateRefund = 'WITH_REFUND' | 'WITHOUT_REFUND';

export type HotelRateChip = HotelRateBreakfast | HotelRateRefund;

/**
 * O TIPO da tarifa — o eixo que agrupa o painel (hotel → quarto → tipo →
 * fornecedor), derivado dos MESMOS enums dos chips e com a mesma régua:
 * `undefined` é desconhecido, nunca negativo. O rótulo de `ROOM_ONLY` ("Sem
 * café") afirma o eixo do café, então só entra quem o NEGA explicitamente;
 * café mudo — mesmo com reembolso negado — fica em `UNSPECIFIED`.
 */
export type HotelRateTypeKey =
  | 'BREAKFAST_REFUNDABLE'
  | 'BREAKFAST'
  | 'REFUNDABLE'
  | 'ROOM_ONLY'
  | 'UNSPECIFIED';

export function rateTypeKey(rate: Pick<HotelRateView, 'breakfast' | 'refund'>): HotelRateTypeKey {
  const breakfast = rate.breakfast === 'WITH_BREAKFAST';
  const refundable = rate.refund === 'WITH_REFUND';
  if (breakfast && refundable) return 'BREAKFAST_REFUNDABLE';
  if (breakfast) return 'BREAKFAST';
  if (refundable) return 'REFUNDABLE';
  return rate.breakfast === 'WITHOUT_BREAKFAST' ? 'ROOM_ONLY' : 'UNSPECIFIED';
}

export type HotelRateView = {
  id: string;
  channelLabel?: string;
  channelImageUrl?: string;
  planLabel?: string;
  /** Nome CRU do fornecedor — auditoria do agrupamento canônico. A VIEW só o
   *  envia quando difere do título do cartão (o nome do quarto já recomposto);
   *  a UI imprime o que chegar sem checar de novo — não é seu contrato saber
   *  qual é o título. */
  sourceName?: string;
  totalLabel: string;
  total?: HotelMoneyView;
  nightlyLabel?: string;
  nightlyPriceLabel?: string;
  nightlyUnitLabel?: string;
  stayTotalLabel?: string;
  policyLabel?: string;
  /** As regras que ESTA tarifa quebrou, na ordem em que a política as avalia.
   *  `policyLabel` segue genérico (é o chip da linha); isto é o porquê no
   *  popover do escudo. Ausente quando o dado não chega. */
  policyReasons?: string[];
  policyTone: HotelPolicyTone;
  cancellationLabel?: string;
  breakfast?: HotelRateBreakfast;
  refund?: HotelRateRefund;
  negotiated?: boolean;
  remainingRooms?: number;
  available: boolean;
};

export type HotelRoomReceiptView = {
  hotelName: string;
  stayLine?: string;
};

export type HotelRoomsContextView = {
  hotelName: string;
  rating?: HotelRatingView;
  stayLabel?: string;
  /** A régua da política na linha do título ("Acima de R$ 520,00" + a
   *  consequência no popover). Só chega quando o teto da praça existe no dado —
   *  sem ele a linha fica só com o título, nunca com um farol decorativo. */
  policyCeiling?: { label: string; hint: string };
};

export type HotelRoomView = {
  id: string;
  name: string;
  description?: string;
  /** Prosa do fornecedor sobre a acomodação. Só o modal de detalhe mostra: no
   *  card ela roubaria a linha do preço. */
  about?: string;
  amenities: readonly string[];
  photos: readonly HotelPhotoView[];
  capacityLabel?: string;
  bedLabel?: string;
  areaLabel?: string;
  /** Na ordem da recomendação, a melhor primeiro — e a ORDEM INTEIRA conta: com
   *  chips ligados o card mostra a primeira que passa no recorte (#6470). */
  rates: readonly HotelRateView[];
};

export type HotelDetailsReviewView = {
  id: string;
  author: string;
  ratingLabel?: string;
  text: string;
  dateLabel?: string;
};

export type HotelDetailsAmenitySectionView = {
  id: string;
  title: string;
  amenities: readonly HotelAmenityView[];
};

/** Uma faixa da linha do tempo de cancelamento, já escrita por quem sabe o
 *  idioma — o painel só desenha. `detailLabel` ausente ⇒ faixa sem cobrança. */
export type HotelCancellationWindowView = {
  id: string;
  label: string;
  detailLabel?: string;
};

/** A política de cancelamento de UMA tarifa (o fornecedor a define por tarifa,
 *  não por hotel). `rateLabel` diz de qual tarifa se trata e `noteLabel` avisa
 *  que as outras podem diferir — sem esse par, a seção viraria uma promessa
 *  para o hotel inteiro que nenhuma tarifa isolada sustenta. */
export type HotelDetailsCancellationView = {
  headline: string;
  tone: 'free' | 'charged';
  rateLabel?: string;
  windows: readonly HotelCancellationWindowView[];
  noteLabel?: string;
};

export type HotelDetailsView = {
  id: string;
  name: string;
  /** "Hotel com acordo" (#6359): presente só quando o hotel tem acordo. */
  agreedRateLabel?: string;
  summary?: string;
  description?: string;
  photos: readonly HotelPhotoView[];
  rating?: HotelRatingView;
  location?: HotelCoordinates;
  locationLabel?: string;
  locationMap?: HotelMapView;
  distanceLabel?: string;
  travelTimeLabel?: string;
  amenitySections: readonly HotelDetailsAmenitySectionView[];
  cancellation?: HotelDetailsCancellationView;
  reviews: readonly HotelDetailsReviewView[];
};

// A entrada estrutural da projeção da oferta (`results/hotel_offer_source.ts`)
// e as palavras e os fatos que o host entrega a ela.

export type HotelAxis = 'recommended' | 'cheapest' | 'closest';

/** O que os selos leem de uma oferta. */
export interface HotelBadgeSource {
  offerId: string;
  recommended?: boolean;
  /** Hotel com acordo da empresa (#6359). */
  agreedRate?: boolean;
  favorite?: boolean;
  stayedBefore?: boolean;
  stayCount?: number;
  price?: { amount: number };
}

/** Os rótulos dos selos, na língua do host. */
export interface HotelBadgesCopy {
  favorite: string;
  stayedBefore: string;
  /** "Você ficou aqui · N×", só com N ≥ 2. */
  stayedBeforeCount(count: number): string;
  recommended: string;
  cheapest: string;
  closest: string;
  /** Os mesmos eixos quando o bloco é um RECORTE da busca (ver `axisOnCard`). */
  cheapestOnCard: string;
  closestOnCard: string;
  agentPick: string;
  /** "Hotel com acordo" (#6359). */
  agreedRate: string;
}

/** Os códigos de comodidade do catálogo — o mesmo enum do wire, repetido aqui
 *  porque o kit não importa o `domain`. O genui-kit prova a igualdade num teste
 *  de tipo. */
export type HotelAmenityCode =
  | 'wifi'
  | 'breakfast'
  | 'parking'
  | 'pool'
  | 'gym'
  | 'restaurant'
  | 'laundry';

/** Uma comodidade como o fornecedor a manda: `code` quando ela está no catálogo. */
export interface HotelAmenitySource {
  code?: HotelAmenityCode;
  label: string;
}

/** O que a projeção lê de uma oferta. */
export interface HotelOfferSource extends HotelBadgeSource {
  title: string;
  summary?: string;
  address?: string;
  city?: string;
  photos?: readonly { url: string; description?: string }[];
  thumbnailUrl?: string;
  latitude?: number;
  longitude?: number;
  rating?: number;
  reviewCount?: number;
  price?: HotelMoneyView;
  refundable?: boolean;
  refundableRateFrom?: HotelMoneyView;
  policyStatus?: 'within_policy' | 'out_of_policy' | 'unknown';
  amenities?: readonly HotelAmenitySource[];
  hasBreakfastRate?: boolean;
  hasRefundableRate?: boolean;
  /** UMA tarifa com café E reembolso (#5849). */
  hasBreakfastRefundableRate?: boolean;
  /** A política da tarifa mais barata com café / reembolsável /
   *  café+reembolsável (#5820) — o corte "Dentro da política" conjunto. */
  breakfastRateWithinPolicy?: boolean;
  refundableRateWithinPolicy?: boolean;
  breakfastRefundableRateWithinPolicy?: boolean;
}

/** O deslocamento estimado até a âncora da busca. */
export type HotelTravelFact = { minutes: number; mode: 'foot' | 'car' };

/** O que o host sabe de uma oferta e o kit não calcula: a régua de distância é a
 *  do wire, e o motivo da exceção é texto montado com os limites da política. */
export type HotelOfferFacts = {
  distanceKm?: number;
  travel?: HotelTravelFact;
  outOfPolicyReasons?: readonly string[];
};

/** As palavras que a projeção precisa do host. */
export interface HotelOfferViewCopy {
  badges: HotelBadgesCopy;
  amenity(code: HotelAmenityCode): string;
  /** O número com uma casa decimal, no idioma do host ("4,2", "2,4"). */
  decimal(value: number): string;
  /** O nome acessível da nota ("nota 4,2"); recebe o número já formatado. */
  rating(value: string): string;
  /** "2,4 km"; recebe o número já formatado. */
  distance(km: string): string;
  /** "14 min a pé" — o MODO junto do tempo: 17 minutos a pé e 17 de carro são
   *  distâncias opostas. */
  travelTime(travel: HotelTravelFact): string;
  /** O valor formatado, na moeda que ele carrega. */
  money(price: HotelMoneyView): string;
  priceUnavailable: string;
  perNight: string;
  refundable: string;
  /** "Reembolsável a partir de R$ X"; recebe o preço já formatado. */
  refundableFrom(price: string): string;
  outOfPolicy: string;
}
